import React, { useContext, useEffect, useId, useState } from "react";
import {
  Box,
  ButtonStyled,
  FormButtonStyled,
  HeadingStyled,
  LabelStyled,
  SpanStyled,
  TextStyled,
} from "../../../components/styled/Elements";
import AuthHeader from "../../../components/auth/AuthHeader/AuthHeader";
import { Col, Form, Row } from "react-bootstrap";
import { Trans, useTranslation } from "react-i18next";
import { useForm } from "react-hook-form";
import { LoginWrapper } from "../styled";
import { useNavigate } from "react-router-dom";
import ValidatedInput from "../../../components/common/inputFields/ValidatedInput";
import { AuthImage, AuthImageAr, AvatarPlaceholders } from "../../../components/styled/AllImages";
import AuthWelcome from "../../../components/auth/AuthWelcomeCol";
import EmailSentModal from "../../../components/auth/Modal/EmailSentModal";
import PhoneInputField from "../../../components/common/inputFields/PhoneInput";
import AuthApi from "../../../services/api/authApi";
import UsersApi from "../../../services/api/usersApi";
import { emailRegex, fileToBase64Payload } from "../../../utils";
import { toast } from "react-toastify";
import { SelectionDropdown } from "../../../components/styled/FormElements";
import ValidationController from "../../../components/common/inputFields/ValidationController";
import { MultiSelectionDropdown } from "../../../components/common/dropdowns/MultiSelectDropdown";
import DocumentUpload from "../../../components/common/uploadFiles/documentUpload";
import { specializationDropdownList } from "../../../helpers/webConstants";
import { AuthContext } from "../../../context/authContext";
import { useUser } from "../../../context/userContext";
import styled from "styled-components";

const SignUp = () => {
  const { t, i18n } = useTranslation();
  const {
    register,
    control,
    watch,
    setValue,
    handleSubmit,
    formState: { errors },
  } = useForm({
    defaultValues: {
      specialization: 4,
      we_operate_in: [],
    },
  });
  const navigate = useNavigate();
  const { login } = useContext(AuthContext);
  const { fetchUserInfo } = useUser();
  const [loading, setLoading] = useState(false);
  const [emailSentModalShow, setEmailSentModalShow] = useState(false);
  const [isVisible, setIsVisible] = useState(window.innerWidth >= 992);
  const [cities, setCities] = useState([]);
  const [profilePreview, setProfilePreview] = useState(AvatarPlaceholders);
  const [profileFile, setProfileFile] = useState(null);
  const [licenseFile, setLicenseFile] = useState(null);
  const [emailAvailable, setEmailAvailable] = useState(true);
  const [pendingVerification, setPendingVerification] = useState(false);
  const uniqueId = useId();
  const emailValue = watch("email");
  const cityId = watch("city_id");
  const operateIn = watch("we_operate_in") || [];
  const specializationValue = watch("specialization");
  const isVisitorSpecialization = Number(specializationValue) === 4;

  useEffect(() => {
    const handelResize = () => {
      setIsVisible(window.innerWidth >= 992);
    };
    window.addEventListener("resize", handelResize);
    return () => {
      window.removeEventListener("resize", handelResize);
    };
  }, []);

  useEffect(() => {
    const loadCities = async () => {
      try {
        const { data, code } = await UsersApi.getCitiesList();
        if (code === 200 && Array.isArray(data)) {
          setCities(
            data.map((city) => ({
              ...city,
              value: city?.id,
              label: i18n.language === "ar" ? city?.name_ar : city?.name_en,
            }))
          );
        }
      } catch (error) {}
    };
    loadCities();
  }, [i18n.language]);

  useEffect(() => {
    const selected = cities.find((city) => String(city.value) === String(cityId));
    if (selected?.region_id) {
      setValue("province", selected.region_id);
    }
  }, [cityId, cities, setValue]);

  useEffect(() => {
    const email = String(emailValue || "").trim();
    if (!email || !emailRegex.test(email)) {
      setEmailAvailable(true);
      setPendingVerification(false);
      return;
    }
    const timer = setTimeout(async () => {
      try {
        const response = await AuthApi.checkEmail(email);
        if (response?.code === 200) {
          const available = Boolean(response?.data?.available);
          setEmailAvailable(available);
          setPendingVerification(Boolean(response?.data?.pending_verification));
        }
      } catch (error) {}
    }, 600);
    return () => clearTimeout(timer);
  }, [emailValue]);

  useEffect(() => {
    if (!isVisitorSpecialization) return;
    setValue("we_operate_in", []);
    setValue("fal_number", "");
    setValue("fal_id", "");
    setValue("about_me", "");
    setLicenseFile(null);
  }, [isVisitorSpecialization, setValue]);

  const onSubmit = async (formData) => {
    if (!emailAvailable) {
      toast.error(t("email_already_registered"));
      return;
    }
    try {
      setLoading(true);
      const profilePayload = await fileToBase64Payload(profileFile);
      const isVisitor = Number(formData.specialization) === 4;
      const licensePayload = isVisitor
        ? null
        : await fileToBase64Payload(licenseFile);
      const selectedOperate =
        !isVisitor && Array.isArray(operateIn) ? operateIn : [];
      const operateNames = selectedOperate
        .map((id) => cities.find((city) => String(city.value) === String(id))?.name_en)
        .filter(Boolean);

      const payload = {
        name: formData.name,
        email: String(formData.email || "").trim(),
        phone: formData.phone,
        password: formData.password,
        password_confirmation: formData.password_confirmation,
        registration_source: "web",
        language_type: i18n.language === "ar" ? 2 : 1,
        city_id: Number(formData.city_id),
        specialization: Number(formData.specialization),
      };
      if (formData.province) {
        payload.province = Number(formData.province);
      }
      if (!isVisitor) {
        if (operateNames.length > 0) {
          payload.we_operate_in = JSON.stringify(operateNames);
        }
        if (formData.about_me) {
          payload.about_me = formData.about_me;
        }
        if (formData.fal_number) {
          payload.fal_number = formData.fal_number;
        }
        if (formData.fal_id) {
          payload.fal_id = formData.fal_id;
        }
        if (licensePayload?.base64) {
          payload.fal_license_base64 = licensePayload.base64;
          payload.fal_license_filename = licensePayload.filename;
        }
      }
      if (profilePayload?.base64) {
        payload.profile_base64 = profilePayload.base64;
        payload.profile_filename = profilePayload.filename;
      }

      const response = await AuthApi.signup(payload);
      if (response?.code === 200) {
        const token = response?.data?.access_token;
        if (token) {
          login(token);
          await fetchUserInfo();
          toast.success(response.message || t("profile_completed_successfully"));
          navigate("/search-profiles");
          return;
        }
        setEmailSentModalShow(true);
        return;
      }
      if (response?.code === 100 || response?.code === 204 || response?.code === 422) {
        toast.error(response.message || t("something_went_wrong"));
      }
    } catch (error) {
      toast.error(error?.message || t("something_went_wrong"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <LoginWrapper>
      <AuthHeader />
      <Box className="container-fluid containerAuth">
        <Row>
          <Col lg={6} md={12} sm={12} className="px-4 overflow-y-auto authCol">
            <Box className="logSignInner">
              <Box>
                <Box className="mb-4">
                  <HeadingStyled size="24px">{t("SignUpFALHUB")}</HeadingStyled>
                </Box>
                <Form onSubmit={handleSubmit(onSubmit)}>
                  <Box>
                    <PhotoRow className="mb-4">
                      <button
                        type="button"
                        className="photo-avatar"
                        onClick={(event) => {
                          event.preventDefault();
                          document.getElementById(`signup-photo-${uniqueId}`)?.click();
                        }}
                        aria-label={t("signup_add_profile_photo_optional")}
                      >
                        <img src={profilePreview} alt="" />
                      </button>
                      <Box className="photo-actions">
                        <ButtonStyled
                          label={t("signup_add_profile_photo_optional")}
                          bg="var(--white)"
                          border="var(--lightGrey2)"
                          color="var(--themeColor)"
                          type="button"
                          width="auto"
                          clName="photo-upload-btn"
                          onClick={(event) => {
                            event.preventDefault();
                            document.getElementById(`signup-photo-${uniqueId}`)?.click();
                          }}
                        />
                        <input
                          id={`signup-photo-${uniqueId}`}
                          type="file"
                          accept="image/png,image/jpeg,image/jpg,image/webp"
                          style={{ display: "none" }}
                          onChange={(e) => {
                            const file = e.target.files?.[0];
                            if (!file) {
                              return;
                            }
                            setProfileFile(file);
                            setProfilePreview(URL.createObjectURL(file));
                          }}
                        />
                        <TextStyled size="12px" color="var(--darkGrey)" className="photo-hint">
                          {t("signup_photo_optional_hint")}
                        </TextStyled>
                      </Box>
                    </PhotoRow>
                    <ValidatedInput
                      name="name"
                      label={"FullName"}
                      type="input"
                      fieldType="text"
                      placeholder={"FullName"}
                      className={"mb-3 combined flex-wrap p-0"}
                      staric="staric"
                      register={register}
                      errors={errors}
                      validationRules={{
                        required: true,
                      }}
                    />
                    <ValidatedInput
                      name="email"
                      label={"email"}
                      type="input"
                      fieldType="text"
                      placeholder={"email"}
                      className={"mb-3 combined flex-wrap p-0"}
                      staric="staric"
                      register={register}
                      errors={errors}
                      validationRules={{
                        required: true,
                        pattern: {
                          value: emailRegex,
                          message: "invalidEmail",
                        },
                      }}
                    />
                    {!emailAvailable ? (
                      <TextStyled size="13px" color="var(--danger, #c00)" className="mb-3">
                        {t("email_already_registered")}
                      </TextStyled>
                    ) : null}
                    <PhoneInputField
                      name="phone"
                      label={"phone"}
                      placeholder={"phone"}
                      register={register}
                      control={control}
                      errors={errors}
                      staric="staric"
                      validationRules={{
                        required: true,
                      }}
                      className={"mb-3 combined flex-wrap p-0"}
                    />
                    <Box className="formEleContainer mb-3">
                      <LabelStyled className="labelForm">
                        {t("City")}
                        <span className="staric">*</span>
                      </LabelStyled>
                      <ValidationController
                        name={"city_id"}
                        control={control}
                        errors={errors}
                        validationConfig={{
                          required: `fieldRequired`,
                        }}
                        renderer={({ field }) => (
                          <SelectionDropdown
                            name={field.name}
                            placeholder={t("please_select_city")}
                            options={cities}
                            value={field.value}
                            onChange={(e) => field.onChange(e.target.value)}
                          />
                        )}
                      />
                    </Box>
                    <Box className="formEleContainer mb-3">
                      <LabelStyled className="labelForm">
                        {t("Specialization")}
                        <span className="staric">*</span>
                      </LabelStyled>
                      <ValidationController
                        name={"specialization"}
                        control={control}
                        errors={errors}
                        validationConfig={{
                          required: `fieldRequired`,
                        }}
                        renderer={({ field }) => (
                          <SelectionDropdown
                            name={field.name}
                            placeholder={t("select_specialization")}
                            options={specializationDropdownList}
                            value={field.value}
                            onChange={(e) => field.onChange(e.target.value)}
                          />
                        )}
                      />
                    </Box>
                    {!isVisitorSpecialization && (
                      <>
                    <Box className="formEleContainer mb-3">
                      <LabelStyled className="labelForm">{t("WeOperatein")}</LabelStyled>
                      <ValidationController
                        name={"we_operate_in"}
                        control={control}
                        errors={errors}
                        validationConfig={{}}
                        renderer={({ field }) => (
                          <MultiSelectionDropdown
                            name={field.name}
                            placeholder={t("SelectOne")}
                            options={cities}
                            value={field.value || []}
                            onChange={(selected) => field.onChange(selected)}
                          />
                        )}
                      />
                    </Box>
                    <ValidatedInput
                      name="fal_number"
                      label={"FalNo"}
                      type="input"
                      fieldType="text"
                      placeholder={"EnterFAL"}
                      className={"mb-3 combined flex-wrap p-0"}
                      register={register}
                      errors={errors}
                      validationRules={{
                        required: false,
                      }}
                    />
                    <ValidatedInput
                      name="fal_id"
                      label={"id"}
                      type="input"
                      fieldType="text"
                      placeholder={"EnteryourID"}
                      className={"mb-3 combined flex-wrap p-0"}
                      register={register}
                      errors={errors}
                      validationRules={{
                        required: false,
                      }}
                    />
                    <Box className="formEleContainer mb-3">
                      <LabelStyled className="labelForm">{t("UploadFALLicense")}</LabelStyled>
                      <DocumentUpload
                        UploadImage="UploadFALLicense"
                        UploadSecTxt="ordraganddrop"
                        accept=".pdf,image/png,image/jpeg,image/jpg,image/webp"
                        onChange={(file) => setLicenseFile(file)}
                        selectedFile={licenseFile}
                        setSelectedFile={setLicenseFile}
                      />
                    </Box>
                    <ValidatedInput
                      name="about_me"
                      label={"Aboutme"}
                      type="textarea"
                      fieldType="text"
                      placeholder={"Writeaboutyourself"}
                      className={"mb-3 combined"}
                      register={register}
                      errors={errors}
                      validationRules={{
                        required: false,
                        maxLength: {
                          value: 250,
                          message: "maximum_250_characters_allowed",
                        },
                      }}
                    />
                      </>
                    )}
                    <ValidatedInput
                      name="password"
                      label={"password"}
                      type="input"
                      fieldType="password"
                      placeholder={"passwordPlaceholder"}
                      className={"mb-3 combined flex-wrap p-0"}
                      staric="staric"
                      errors={errors}
                      register={register}
                      validationRules={{
                        required: true,
                        inline: true,
                        minLength: {
                          value: 7,
                          message: "lengthGreaterThen",
                        },
                      }}
                    />
                    <ValidatedInput
                      name="password_confirmation"
                      label={"confirm_password"}
                      type="input"
                      fieldType="password"
                      placeholder={"confirmPasswordPlaceholder"}
                      className={"mb-3 combined flex-wrap p-0"}
                      staric="staric"
                      register={register}
                      errors={errors}
                      validationRules={{
                        required: true,
                        validate: (value) =>
                          value === watch("password") || "password_do_not_match",
                      }}
                    />
                    <Box className="mt-4 mb-3">
                      <Trans
                        i18nKey="signupAgreement"
                        components={{
                          1: (
                            <SpanStyled
                              onClick={() => navigate("/terms-conditions")}
                              color="var(--themeColorSecondary)"
                              style={{
                                cursor: "pointer",
                              }}
                            />
                          ),
                          2: (
                            <SpanStyled
                              onClick={() => navigate("/privacy-policy")}
                              color="var(--themeColorSecondary)"
                              style={{
                                cursor: "pointer",
                              }}
                            />
                          ),
                        }}
                      />
                    </Box>
                  </Box>
                  <FormButtonStyled
                    label={t(pendingVerification ? "continue_registration" : "sign_up")}
                    color="var(--white)"
                    bg="var(--themeColor)"
                    className="w-100"
                    clName="w-100 mb-4"
                    type="submit"
                    isLoading={loading}
                    disabled={!emailAvailable}
                  />
                </Form>
              </Box>
              <Box className="d-flex justify-content-center justify-content-center mb-4">
                <Box className="d-block w-100 text-center">
                  <TextStyled
                    className="d-inline-block w-auto"
                    color="var(--darkGrey)"
                  >
                    {t("already_have_account")} &nbsp;
                  </TextStyled>
                  <TextStyled
                    className="cursor-pointer d-inline-block w-auto"
                    color="var(--themeColorSecondary)"
                    onClick={() => navigate("/login")}
                  >
                    {t("sign_in")}
                  </TextStyled>
                </Box>
              </Box>
            </Box>
          </Col>
          {isVisible && <AuthWelcome logSignImg={i18n.language === "en" ? AuthImage : AuthImageAr} />}
        </Row>
      </Box>
      <EmailSentModal
        show={emailSentModalShow}
        onHide={() => setEmailSentModalShow(false)}
      />
    </LoginWrapper>
  );
};

const PhotoRow = styled.div`
  display: flex;
  align-items: center;
  width: 100%;
  text-align: start;
  gap: 20px;
  .photo-avatar {
    flex-shrink: 0;
    width: 80px;
    height: 80px;
    padding: 0;
    border: 0;
    background: transparent;
    border-radius: 50%;
    overflow: hidden;
    cursor: pointer;
    img {
      display: block;
      width: 80px;
      height: 80px;
      object-fit: cover;
      border-radius: 50%;
    }
  }
  .photo-actions {
    min-width: 0;
    flex: 1;
  }
  .photo-upload-btn {
    width: auto;
    max-width: 100%;
    button {
      width: auto;
      white-space: normal;
    }
  }
  .photo-hint {
    display: block;
    margin-top: 8px;
  }
  @media (max-width: 575px) {
    flex-wrap: wrap;
    .photo-actions {
      width: 100%;
    }
  }
`;

export default SignUp;
